import React, { Component } from 'react'
import {
    Alert,
    Badge,
    Button,
    Descriptions,
    Divider,
    Input,
    Modal,
    Space,
    Spin,
    Tag,
    Timeline,
    Typography,
    message,
} from 'antd'
import {
    DeleteOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import {
    executeVibeProjectDelete,
    getVibeProjectDeleteStatus,
    previewVibeProjectDelete,
    retryVibeProjectDelete,
} from '../../request'
import {
    VIBE_PROJECT_DELETE_POLL_INTERVAL,
    VIBE_PROJECT_DELETE_STATUS_META,
    canRetryVibeProjectDelete,
    getVibeProjectDeleteConfirmation,
    getVibeProjectDeleteResourceItems,
    getVibeProjectDeleteStepText,
    isVibeProjectDeleteActive,
    isVibeProjectDeleteCompleted,
    isVibeProjectDeleteTask,
} from './vibeProjectDelete'

const { Text, Paragraph } = Typography

const initialDeleteState = () => ({
    previewLoading: false,
    previewError: false,
    preview: null,
    task: null,
    confirmation: '',
    executing: false,
    retrying: false,
    statusLoading: false,
    statusLoadError: false,
    completionNotified: false,
})

export default class VibeProjectDeleteDialog extends Component {
    state = initialDeleteState()

    pollTimer = null

    requestVersion = 0

    unmounted = false

    componentDidUpdate(prevProps) {
        const previousProjectId = prevProps.project?.id
        const projectId = this.props.project?.id
        if (this.props.visible && (!prevProps.visible || String(previousProjectId) !== String(projectId))) {
            this.loadPreview()
        }
        if (!this.props.visible && prevProps.visible) {
            this.requestVersion += 1
            this.stopPolling()
        }
    }

    componentWillUnmount() {
        this.unmounted = true
        this.requestVersion += 1
        this.stopPolling()
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearTimeout(this.pollTimer)
            this.pollTimer = null
        }
    }

    schedulePolling = () => {
        this.stopPolling()
        if (!this.props.visible || this.unmounted) return
        this.pollTimer = setTimeout(this.loadTaskStatus, VIBE_PROJECT_DELETE_POLL_INTERVAL)
    }

    loadPreview = () => {
        const projectId = this.props.project?.id
        if (!projectId) return
        const requestVersion = ++this.requestVersion
        this.stopPolling()
        this.setState({
            ...initialDeleteState(),
            previewLoading: true,
        })
        previewVibeProjectDelete({ id: projectId })
            .then(result => {
                if (this.unmounted || requestVersion !== this.requestVersion) return
                if (isVibeProjectDeleteTask(result)) {
                    this.acceptTask(result, false)
                    return
                }
                this.setState({
                    preview: result || null,
                    previewLoading: false,
                    previewError: false,
                })
            })
            .catch(() => {
                if (this.unmounted || requestVersion !== this.requestVersion) return
                this.setState({ previewLoading: false, previewError: true })
            })
    }

    acceptTask = (task, notifyCompletion = true) => {
        const status = task?.status
        this.setState({
            task,
            preview: null,
            previewLoading: false,
            previewError: false,
            statusLoading: false,
            statusLoadError: false,
        }, () => {
            if (isVibeProjectDeleteActive(status)) {
                this.schedulePolling()
                return
            }
            this.stopPolling()
            if (isVibeProjectDeleteCompleted(status) && !this.state.completionNotified) {
                this.setState({ completionNotified: true })
                if (notifyCompletion) message.success('VibeCoding 项目已彻底删除')
                if (this.props.onDeleted) this.props.onDeleted(task)
            }
        })
    }

    loadTaskStatus = () => {
        const projectId = this.props.project?.id
        if (!projectId || !this.props.visible || this.state.statusLoading) return
        this.setState({ statusLoading: true, statusLoadError: false })
        getVibeProjectDeleteStatus({ id: projectId })
            .then(task => {
                if (this.unmounted || !this.props.visible) return
                this.acceptTask(task)
            })
            .catch(() => {
                if (this.unmounted || !this.props.visible) return
                this.stopPolling()
                this.setState({ statusLoading: false, statusLoadError: true })
            })
    }

    executeDelete = () => {
        const projectId = this.props.project?.id
        const confirmationSlug = this.getConfirmationSlug()
        if (!projectId || this.state.confirmation !== confirmationSlug || this.state.executing) return
        this.setState({ executing: true })
        executeVibeProjectDelete({
            id: projectId,
            confirmation: this.state.confirmation,
        })
            .then(task => {
                message.warning('物理删除任务已启动，请等待全部资源清理完成')
                this.acceptTask(task)
            })
            .catch(() => {})
            .finally(() => {
                if (!this.unmounted) this.setState({ executing: false })
            })
    }

    retryDelete = () => {
        const projectId = this.props.project?.id
        const confirmationSlug = this.getConfirmationSlug()
        if (!projectId || this.state.confirmation !== confirmationSlug || this.state.retrying) return
        this.setState({ retrying: true })
        retryVibeProjectDelete({
            id: projectId,
            confirmation: this.state.confirmation,
        })
            .then(task => {
                message.warning('删除任务已重新进入执行队列')
                this.acceptTask(task)
            })
            .catch(() => {})
            .finally(() => {
                if (!this.unmounted) this.setState({ retrying: false })
            })
    }

    getConfirmationSlug = () => getVibeProjectDeleteConfirmation(
        this.state.preview || this.state.task,
        this.props.project,
    )

    getPreviewBlockingReason = () => {
        const status = this.state.preview?.project?.status
        if (status === 'provisioning') return '项目正在接入中，暂时不能物理删除'
        if (status === 'deleting') return '项目已处于删除中但未找到删除任务，请先人工核查'
        if (status === 'deleted') return '项目已软删除且没有资源快照，不能直接物理删除'
        return ''
    }

    close = () => {
        if (this.state.executing || this.state.retrying) return
        if (this.props.onClose) this.props.onClose()
    }

    renderInventory = () => {
        const { preview } = this.state
        if (!preview) return null
        const project = preview.project || {}
        const resources = getVibeProjectDeleteResourceItems(preview.resourceCounts)
        const usernames = Array.isArray(preview.grantMysqlUsernames) ? preview.grantMysqlUsernames : []
        const physicalTables = Array.isArray(preview.datasetPhysicalTables) ? preview.datasetPhysicalTables : []
        const ossKeys = Array.isArray(preview.ossObjectKeys) ? preview.ossObjectKeys : []
        const warnings = Array.isArray(preview.warnings) ? preview.warnings : []
        const deletionOrder = Array.isArray(preview.deletionOrder) ? preview.deletionOrder : []

        return (
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Alert
                    type="error"
                    showIcon
                    icon={<ExclamationCircleOutlined />}
                    message="这是不可恢复的物理删除"
                    description="确认后系统将异步清理该项目的运行环境、数据、权限、文件、GitLab 仓库及业务元数据。删除过程中请勿手工修改项目资源。"
                />

                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="项目">{project.projectName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Slug"><Text code>{project.projectSlug || '-'}</Text></Descriptions.Item>
                    <Descriptions.Item label="项目用途">
                        {project.projectKind === 'dataset' ? 'AIC项目' : 'Vibe项目'}
                    </Descriptions.Item>
                    <Descriptions.Item label="运行方式">
                        {project.deploymentMode === 'server' ? '服务器部署' : '仅本地运行'}
                    </Descriptions.Item>
                    <Descriptions.Item label="数据库" span={2}>
                        <Text code>{project.mysqlDatabaseName || '未配置'}</Text>
                    </Descriptions.Item>
                    {project.gitlabProjectPath ? (
                        <Descriptions.Item label="GitLab 项目" span={2}>
                            <Text code>{project.gitlabProjectPath}</Text>
                        </Descriptions.Item>
                    ) : null}
                    {preview.inventoryFilePath ? (
                        <Descriptions.Item label="部署清单" span={2}>
                            <Text code>{preview.inventoryFilePath}</Text>
                        </Descriptions.Item>
                    ) : null}
                </Descriptions>

                {preview.shouldDropDatabase ? (
                    <Alert
                        type="error"
                        showIcon
                        message={`将物理删除独立数据库：${project.mysqlDatabaseName || '-'}`}
                        description={preview.databaseDeleteReason || '该数据库由当前项目独占且由系统创建'}
                    />
                ) : (
                    <Alert
                        type={project.projectKind === 'dataset' ? 'warning' : 'info'}
                        showIcon
                        message={project.projectKind === 'dataset'
                            ? `不会删除共享数据库 ${project.mysqlDatabaseName || ''}，但会删除当前数据集的受管物理表`
                            : '不会物理删除数据库'}
                        description={preview.databaseDeleteReason || '数据库不属于当前项目独占的受管资源'}
                    />
                )}

                <div>
                    <Text strong>将删除的关联资源</Text>
                    <div style={{ marginTop: 8 }}>
                        <Space wrap>
                            {preview.serverCleanupRequired ? <Tag color="red">服务器部署资源</Tag> : null}
                            {project.gitlabProjectId ? <Tag color="red">GitLab 项目</Tag> : null}
                            {resources.map(item => (
                                <Tag color="volcano" key={item.key}>{item.label} {item.count}</Tag>
                            ))}
                            {!preview.serverCleanupRequired && !project.gitlabProjectId && !resources.length
                                ? <Text type="secondary">仅清理项目业务元数据</Text>
                                : null}
                        </Space>
                    </div>
                    {usernames.length ? (
                        <Paragraph type="secondary" style={{ margin: '8px 0 0' }}>
                            回收数据库账号授权：{usernames.join('、')}
                        </Paragraph>
                    ) : null}
                    {physicalTables.length ? (
                        <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
                            删除受管物理表：{physicalTables.join('、')}
                        </Paragraph>
                    ) : null}
                    {ossKeys.length ? (
                        <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
                            删除 OSS 对象：{ossKeys.length} 个
                        </Paragraph>
                    ) : null}
                </div>

                {deletionOrder.length ? (
                    <div>
                        <Text strong>执行顺序</Text>
                        <Timeline style={{ marginTop: 12 }}>
                            {deletionOrder.map(step => (
                                <Timeline.Item key={step}>{getVibeProjectDeleteStepText(step)}</Timeline.Item>
                            ))}
                        </Timeline>
                    </div>
                ) : null}

                {warnings.length ? (
                    <Alert
                        type="warning"
                        showIcon
                        message="删除注意事项"
                        description={warnings.map(item => <div key={item}>• {item}</div>)}
                    />
                ) : null}
            </Space>
        )
    }

    renderTask = () => {
        const { task, statusLoading, statusLoadError } = this.state
        if (!task) return null
        const statusMeta = VIBE_PROJECT_DELETE_STATUS_META[task.status]
            || { text: task.status || '-', color: 'default' }
        return (
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
                <Alert
                    type={isVibeProjectDeleteCompleted(task.status)
                        ? 'success'
                        : (canRetryVibeProjectDelete(task.status) ? 'error' : 'warning')}
                    showIcon
                    message={statusMeta.text}
                    description={isVibeProjectDeleteActive(task.status)
                        ? '删除任务正在后台执行。可以关闭弹窗，稍后重新打开查看状态。'
                        : (task.errorMessage || '全部资源已经清理完成，项目不可恢复。')}
                />
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="项目">{task.projectName || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Slug"><Text code>{task.projectSlug || '-'}</Text></Descriptions.Item>
                    <Descriptions.Item label="任务状态">
                        <Badge status={task.status === 'completed' ? 'success' : (task.status === 'failed' ? 'error' : 'processing')} />
                        <Tag color={statusMeta.color}>{statusMeta.text}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="当前步骤">{getVibeProjectDeleteStepText(task.currentStep)}</Descriptions.Item>
                    <Descriptions.Item label="重试次数">{Number(task.retryCount) || 0}</Descriptions.Item>
                    <Descriptions.Item label="更新时间">{task.updateTime || '-'}</Descriptions.Item>
                    {task.cleanupPipelineId ? (
                        <Descriptions.Item label="清理 Pipeline" span={2}>
                            #{task.cleanupPipelineId} / {task.cleanupPipelineStatus || '-'}
                        </Descriptions.Item>
                    ) : null}
                </Descriptions>
                {statusLoadError ? (
                    <Alert
                        type="error"
                        showIcon
                        message="删除状态刷新失败"
                        description="自动轮询已暂停，请确认网络正常后手动刷新。"
                        action={<Button size="small" loading={statusLoading} onClick={this.loadTaskStatus}>重新刷新</Button>}
                    />
                ) : null}
            </Space>
        )
    }

    renderConfirmation = () => {
        const { preview, task, confirmation } = this.state
        if (!preview && !canRetryVibeProjectDelete(task?.status)) return null
        const confirmationSlug = this.getConfirmationSlug()
        if (!confirmationSlug) return null
        return (
            <div>
                <Divider />
                <Alert
                    type="error"
                    showIcon
                    message={task ? '重试仍会从失败步骤继续执行不可逆清理' : '最后确认'}
                    description={(
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                请输入项目 Slug <Text code copyable>{confirmationSlug}</Text>，完全一致后才能{task ? '重试' : '开始删除'}。
                            </div>
                            <Input
                                value={confirmation}
                                autoComplete="off"
                                placeholder={confirmationSlug}
                                onChange={event => this.setState({ confirmation: event.target.value })}
                            />
                        </div>
                    )}
                />
            </div>
        )
    }

    renderFooter = () => {
        const {
            preview,
            task,
            confirmation,
            executing,
            retrying,
            previewLoading,
            statusLoading,
        } = this.state
        const confirmationMatches = confirmation === this.getConfirmationSlug()
        const blockingReason = this.getPreviewBlockingReason()
        const buttons = [
            <Button key="close" onClick={this.close} disabled={executing || retrying}>关闭</Button>,
        ]
        if (preview) {
            buttons.push(
                <Button
                    key="delete"
                    type="primary"
                    danger
                    icon={<DeleteOutlined />}
                    loading={executing}
                    disabled={!confirmationMatches || Boolean(blockingReason)}
                    onClick={this.executeDelete}
                >
                    确认永久删除
                </Button>,
            )
        } else if (canRetryVibeProjectDelete(task?.status)) {
            buttons.push(
                <Button
                    key="retry"
                    type="primary"
                    danger
                    icon={<ReloadOutlined />}
                    loading={retrying}
                    disabled={!confirmationMatches}
                    onClick={this.retryDelete}
                >
                    从失败步骤重试
                </Button>,
            )
        } else if (isVibeProjectDeleteActive(task?.status)) {
            buttons.push(
                <Button
                    key="refresh"
                    type="primary"
                    icon={<ReloadOutlined />}
                    loading={statusLoading}
                    onClick={this.loadTaskStatus}
                >
                    刷新状态
                </Button>,
            )
        } else if (!previewLoading && !task) {
            buttons.push(<Button key="reload" type="primary" onClick={this.loadPreview}>重新预检</Button>)
        }
        return buttons
    }

    render() {
        const { visible, project } = this.props
        const { previewLoading, previewError } = this.state
        const blockingReason = this.getPreviewBlockingReason()
        return (
            <Modal
                visible={visible}
                title={<span><DeleteOutlined style={{ color: '#ff4d4f' }} /> 永久删除 VibeCoding 项目：{project?.projectName || '-'}</span>}
                width={820}
                footer={this.renderFooter()}
                onCancel={this.close}
                closable={!this.state.executing && !this.state.retrying}
                maskClosable={false}
                keyboard={!this.state.executing && !this.state.retrying}
                destroyOnClose
            >
                <Spin spinning={previewLoading} tip="正在预检项目资源...">
                    {previewError ? (
                        <Alert
                            type="error"
                            showIcon
                            message="删除预检失败"
                            description="未获取到完整资源清单，系统不会允许执行删除。"
                        />
                    ) : null}
                    {blockingReason ? <Alert type="error" showIcon message={blockingReason} style={{ marginBottom: 14 }} /> : null}
                    {this.renderInventory()}
                    {this.renderTask()}
                    {this.renderConfirmation()}
                </Spin>
            </Modal>
        )
    }
}
